/**
 * Step metadata for onboarding wizard screens
 *
 * Provides headline, description, tooltip, and context callout content
 * for each of the 7 onboarding steps. Referenced by OnboardingModalLayout
 * to render per-step contextual information.
 */

import type { OnboardingCategory } from "../hooks/useOnboardingNavigation";

export interface StepMetadata {
  /** Step headline shown above the form (e.g., "Let's get to know you") */
  headline: string;
  /** Short description paragraph below the headline */
  description: string;
  /** Tooltip text for the "?" help button */
  tooltipContent: string;
  /** Accessible label for the tooltip trigger button */
  tooltipAriaLabel: string;
  /** Context callout text (💡 prefix, amber background) */
  contextCalloutText: string;
}

export const STEP_METADATA: Record<OnboardingCategory, StepMetadata> = {
  profile: {
    headline: "Let's get to know you",
    description:
      "Your age, location, and goals shape your projections. This stays private.",
    tooltipContent:
      "We use your age, province, and retirement goals to calculate your timeline, model taxes, compare you against Canadian averages, and set appropriate defaults for life expectancy and government benefits.",
    tooltipAriaLabel: "Why we ask about your profile",
    contextCalloutText:
      "Your province determines your marginal tax rate — this significantly impacts your retirement income.",
  },
  investableAssets: {
    headline: "Your Savings & Investments",
    description:
      "The backbone of your retirement plan. These accounts grow over time and fund your retirement withdrawals.",
    tooltipContent:
      'Your investable assets — RRSP, TFSA, non-registered accounts, pension plans, and cash — are the "retirement engine." We project their growth and model how you\'ll draw them down in retirement.',
    tooltipAriaLabel: "Why we ask about your savings and investments",
    contextCalloutText:
      "Canadian median investable assets for your age bracket.",
  },
  realEstateAssets: {
    headline: "Property, Business & Other Assets",
    description:
      "Your home, investment properties, business interests, and other valuable assets.",
    tooltipContent:
      "Real estate and business interests affect your net worth and may provide rental income or a downsizing option. We model appreciation separately from your investment portfolio.",
    tooltipAriaLabel: "Why we ask about property and other assets",
    contextCalloutText:
      "Your home doesn't directly fund retirement unless you sell — but being mortgage-free reduces expenses significantly.",
  },
  liabilities: {
    headline: "Your Debts & Obligations",
    description:
      "Mortgages, loans, and credit cards. We'll calculate payoff timelines and how debt affects your retirement date.",
    tooltipContent:
      "Outstanding debts reduce your net worth and generate interest costs. By tracking balances and interest rates, we model when each debt is paid off and how it impacts your retirement timeline.",
    tooltipAriaLabel: "Why we ask about your debts",
    contextCalloutText:
      "We factor in interest costs and payoff timelines. Being debt-free at retirement removes a huge expense burden.",
  },
  income: {
    headline: "Your Income Streams",
    description:
      "Employment, government benefits, pension, rental, and investment income — now and in retirement.",
    tooltipContent:
      "Your income determines how much you can save, when you can retire, and what benefits you'll qualify for. We model income streams separately because some stop at retirement, some start, and some continue.",
    tooltipAriaLabel: "Why we ask about your income",
    contextCalloutText:
      "CPP and OAS are auto-estimated from your profile and income data. You can override the estimates in Settings.",
  },
  expenses: {
    headline: "Your Living Expenses",
    description:
      "Monthly spending is the #1 driver of your retirement forecast. Be honest — this is crucial for accurate projections.",
    tooltipContent:
      "Your spending determines how much retirement income you'll need. We project these expenses into the future with inflation to estimate your total retirement funding requirement.",
    tooltipAriaLabel: "Why we ask about your expenses",
    contextCalloutText:
      "These expenses continue in retirement unless you adjust them in Planning Assumptions. Inflation is factored in automatically.",
  },
  assumptions: {
    headline: "Market & Planning Assumptions",
    description:
      "These rates and strategies shape your projections. Defaults work for most Canadians — customize if you know your situation.",
    tooltipContent:
      "Return rates, inflation, withdrawal strategy, and other assumptions drive the math behind your projections. We use Canadian historical averages as defaults.",
    tooltipAriaLabel: "Why we ask about planning assumptions",
    contextCalloutText:
      "Expected Return: 5-7% historically for balanced portfolios. Inflation: Bank of Canada targets 2%.",
  },
};
